import { unwrapAffiliateUrl as unwrapTrackedUrl } from "./affiliate";

const DIRECT_SELLER_HOST_PATTERNS = [
  "tixr.com",
  "ticketmaster.",
  "dice.fm",
  "eventbrite.",
  "axs.com",
  "seetickets.",
  "ticketweb.",
];

const AGGREGATOR_HOST_PATTERNS = ["bandsintown.com", "songkick.com"];

function parseUrl(link: string) {
  try {
    return new URL(link);
  } catch {
    return null;
  }
}

function unwrapAffiliateUrl(link: string) {
  const url = parseUrl(link);
  const wrappedUrl = url?.searchParams.get("u");

  if (!wrappedUrl) return link;

  try {
    return new URL(wrappedUrl).toString();
  } catch {
    return link;
  }
}

function providerScore(link: string) {
  const url = link.toLowerCase();

  if (url.includes("songkick.com/tickets/")) return 20;

  const directSellerIndex = DIRECT_SELLER_HOST_PATTERNS.findIndex((pattern) =>
    url.includes(pattern),
  );
  if (directSellerIndex >= 0) return directSellerIndex;

  const aggregatorIndex = AGGREGATOR_HOST_PATTERNS.findIndex((pattern) =>
    url.includes(pattern),
  );
  if (aggregatorIndex >= 0) return 50 + aggregatorIndex;

  return 30;
}

function contentScore(link: string) {
  const url = link.toLowerCase();

  if (url.includes("parking") || url.includes("plaza-de-parking")) return 20;
  if (url.includes("vip")) return 10;

  return 0;
}

export function normalizeTicketLinks(links: string[] | null | undefined) {
  if (!links) return [];
  return dedupeTicketLinks(links.map(unwrapAffiliateUrl));
}

export function sortTicketLinks(links: string[] | null | undefined) {
  return normalizeTicketLinks(links)
    .map((link, index) => ({ link, index }))
    .sort((a, b) => {
      const providerDiff = providerScore(a.link) - providerScore(b.link);
      if (providerDiff !== 0) return providerDiff;

      const contentDiff = contentScore(a.link) - contentScore(b.link);
      if (contentDiff !== 0) return contentDiff;

      return a.index - b.index;
    })
    .map(({ link }) => link);
}

/** Query parameters by which an affiliate programme credits a sale to its
 *  partner: Impact's (Ticketmaster's `clickId`, `irgwc`, `ircid`, `afsrc`,
 *  with Ticketmaster's own `camefrom` source), Awin's `awc`, Eventim's
 *  `affiliate`, and the usual campaign tags. Songkick's and Bandsintown's
 *  links carry theirs. */
const AFFILIATE_PARAMS = new Set([
  "clickid",
  "irclickid",
  "irgwc",
  "ircid",
  "afsrc",
  "camefrom",
  "awc",
  "affiliate",
  "sharedid",
  "subid1",
  "subid2",
  "subid3",
  "gclid",
  "fbclid",
]);

/** DICE's partner parameters: only DICE's, as other sites use the names. */
const DICE_AFFILIATE_PARAMS = new Set(["pid", "cid1", "cid2"]);

/** DICE's partner checkout path, `/partner/tickets/event/{slug}`: its
 *  public event page is `/event/{slug}`. */
const DICE_PARTNER_PATH = /^\/partner\/tickets\/event\/([^/]+)\/?$/;

/**
 * A seller's address, unwrapped from any known affiliate tracker in older
 * saved links, without the affiliate parameters another programme
 * put on it (`AFFILIATE_PARAMS`, `utm_*`), so a sale is not credited to
 * Songkick or Bandsintown when Lissen sends the fan; a DICE partner link
 * becomes its public event page. The sellers run affiliate programmes of
 * their own (Ticketmaster through Impact, Eventim through Awin, DICE's
 * partners). Any other address is returned as it was.
 */
export function stripAffiliateParams(link: string): string {
  const url = parseUrl(unwrapTrackedUrl(link).trim());
  if (!url || !/^https?:$/.test(url.protocol)) return link;
  const dice = /(^|\.)dice\.fm$/i.test(url.hostname);
  for (const name of [...url.searchParams.keys()]) {
    const lower = name.toLowerCase();
    if (AFFILIATE_PARAMS.has(lower) || lower.startsWith("utm_") || (dice && DICE_AFFILIATE_PARAMS.has(lower)))
      url.searchParams.delete(name);
  }
  if (dice) {
    const slug = DICE_PARTNER_PATH.exec(url.pathname)?.[1];
    if (slug) url.pathname = `/event/${slug}`;
  }
  return url.toString();
}

/** Per-click tracking parameters that credit no sale: Branch's deep-link
 *  match (`_branch_match_id`, `_branch_referrer`) and the snake-case
 *  `click_id`. SoundCharts sends a ticket link again with fresh values on
 *  every listing it read, so one event page arrived hundreds of times. */
const CLICK_TRACKING_PARAM = /^(_branch_|click_id$)/i;

/**
 * A ticket link's identity for the duplicate check: the seller's address
 * without its affiliate parameters (`stripAffiliateParams`) or per-click
 * tracking (`CLICK_TRACKING_PARAM`), its host lowercased, its fragment
 * dropped and its parameters sorted. Every other parameter stays part of
 * the identity, as one can name a different event, ticket or date.
 * Decided by Erik, 2 Nov 2026: the duplicate check ignores tracking codes.
 */
export function ticketLinkKey(link: string): string {
  const url = parseUrl(stripAffiliateParams(link).trim());
  if (!url) return link.trim();
  for (const name of [...url.searchParams.keys()]) {
    if (CLICK_TRACKING_PARAM.test(name)) url.searchParams.delete(name);
  }
  url.hash = "";
  url.hostname = url.hostname.toLowerCase();
  url.searchParams.sort();
  return url.toString();
}

/**
 * `links` without the later copies of a link (`ticketLinkKey`), in their
 * order. Each kept link is the first copy, as written: the scan and fans'
 * cards read the first link of a kind, and a followed Songkick link's
 * redirect is stored by its exact address.
 */
export function dedupeTicketLinks(links: readonly string[]): string[] {
  const seen = new Set<string>();
  return links.filter((link) => {
    const key = ticketLinkKey(link);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
